import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { SearchBarContextProps } from '@carvana/merch-search-bar/SearchBarContext';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { LoadableState, SavedSearch } from '~/contracts/common';

export type SavedSearchesContextType = {
  fetchSavedSearchTrigger: number;
  savedSearchesForSearchBar: SearchBarContextProps['savedSearches'];
  savedSearchesState: LoadableState<SavedSearch[]>;
  triggerFetchSavedSearches: () => void;
};

export const defaultContext: SavedSearchesContextType = {
  fetchSavedSearchTrigger: 0,
  savedSearchesForSearchBar: undefined,
  savedSearchesState: defaultLoadableState<SavedSearch[]>([]),
  triggerFetchSavedSearches() {}
};

const SavedSearchesContext =
  createContext<SavedSearchesContextType>(defaultContext);

export function formatSavedSearchesForSearchBar(
  savedSearches: SavedSearch[]
): SearchBarContextProps['savedSearches'] {
  return savedSearches.map(({ cvnaId, name, savedSearchId }) => ({
    url: cvnaId,
    name,
    savedSearchId
  }));
}

export function SavedSearchesContextProvider({
  children
}: React.PropsWithChildren) {
  const [fetchSavedSearchTrigger, setTrigger] = useState<number>(
    defaultContext.fetchSavedSearchTrigger
  );
  const savedSearchesState = useLoadableState<SavedSearch[]>([]);
  const [savedSearchesForSearchBar, setSavedSearchesForSearchBar] = useState<
    SearchBarContextProps['savedSearches']
  >([]);

  useEffect(() => {
    if (savedSearchesState.data.length === 0) return;
    setSavedSearchesForSearchBar(
      formatSavedSearchesForSearchBar(savedSearchesState.data)
    );
  }, [savedSearchesState.data]);

  const triggerFetchSavedSearches = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<SavedSearchesContextType>(
    () => ({
      fetchSavedSearchTrigger,
      savedSearchesForSearchBar,
      savedSearchesState,
      triggerFetchSavedSearches
    }),
    [
      fetchSavedSearchTrigger,
      savedSearchesForSearchBar,
      savedSearchesState,
      triggerFetchSavedSearches
    ]
  );

  return (
    <SavedSearchesContext.Provider value={value}>
      {children}
    </SavedSearchesContext.Provider>
  );
}

SavedSearchesContextProvider.displayName = 'SavedSearchesContextProvider';

export default SavedSearchesContext;

export function useSavedSearches() {
  const { savedSearchesState } = useContext(SavedSearchesContext);

  return savedSearchesState.data;
}

export function useSavedSearchesForSearchBar() {
  const { savedSearchesForSearchBar } = useContext(SavedSearchesContext);

  return savedSearchesForSearchBar;
}
